import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImageIcon, Plus, Upload, MoreVertical, Pencil, Trash2, FolderOpen, CheckSquare, X } from 'lucide-react';
import { adminApi } from '@/services/adminApi';
import type { Asset } from '@/types/assets';
import { queryKeys } from '@/lib/queryKeys';
import { AssetGrid, AssetPreviewOverlay, UploadZone, PageHeader, Button, TextField, AlertDialog, EmptyState, LoadingState, Select, Tabs } from '@printtat/ui';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@printtat/ui';

interface AdminCategory {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const AdminAssetsPage = () => {
  const queryClient = useQueryClient();

  // --- State ---
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [deletingCategory, setDeletingCategory] = useState<AdminCategory | null>(null);
  const [deletingAssetId, setDeletingAssetId] = useState<string | null>(null);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [changingCategoryAssetId, setChangingCategoryAssetId] = useState<string | null>(null);
  const [selectedNewCategoryId, setSelectedNewCategoryId] = useState('');
  const [previewAssetId, setPreviewAssetId] = useState<string | null>(null);

  // --- Queries ---
  const { data: categories = [], isLoading: categoriesLoading } = useQuery<AdminCategory[]>({
    queryKey: queryKeys.categories.all(),
    queryFn: () => adminApi.categories.list(),
  });

  const { data: assets = [], isLoading: assetsLoading } = useQuery<Asset[]>({
    queryKey: selectedCategoryId
      ? queryKeys.assets.byCategory(selectedCategoryId)
      : queryKeys.assets.all(),
    queryFn: () => adminApi.assets.list(selectedCategoryId ?? undefined),
  });

  // --- Mutations ---
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'assets'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
    queryClient.invalidateQueries({ queryKey: queryKeys.assets.all() });
    queryClient.invalidateQueries({ queryKey: queryKeys.categories.all() });
  };

  const createCategory = useMutation({
    mutationFn: (name: string) => adminApi.categories.create(name),
    onSuccess: () => {
      invalidateAll();
      setNewCategoryName('');
      setIsCreatingCategory(false);
    },
  });

  const updateCategory = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => adminApi.categories.update(id, { name }),
    onSuccess: () => {
      invalidateAll();
      setEditingCategoryId(null);
    },
  });

  const deleteCategory = useMutation({
    mutationFn: (id: string) => adminApi.categories.delete(id),
    onSuccess: () => {
      if (selectedCategoryId === deletingCategory?.id) setSelectedCategoryId(null);
      invalidateAll();
      setDeletingCategory(null);
    },
  });

  const deleteAsset = useMutation({
    mutationFn: (id: string) => adminApi.assets.delete(id),
    onSuccess: () => {
      invalidateAll();
      setDeletingAssetId(null);
    },
  });

  const renameAsset = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => adminApi.assets.update(id, { name }),
    onSuccess: () => invalidateAll(),
  });

  const bulkDeleteAssets = useMutation({
    mutationFn: (ids: string[]) => adminApi.assets.deleteBulk(ids),
    onSuccess: () => {
      invalidateAll();
      setSelectedAssetIds(new Set());
      setSelectionMode(false);
      setConfirmBulkDelete(false);
    },
  });

  const changeAssetCategory = useMutation({
    mutationFn: ({ id, categoryId }: { id: string; categoryId: string | null }) =>
      adminApi.assets.update(id, { categoryId }),
    onSuccess: () => {
      invalidateAll();
      setChangingCategoryAssetId(null);
      setSelectedNewCategoryId('');
    },
  });

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedAssetIds(new Set());
  };

  const toggleAssetSelection = (id: string, selected: boolean) => {
    setSelectedAssetIds(prev => {
      const next = new Set(prev);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedAssetIds.size === assets.length) {
      setSelectedAssetIds(new Set());
    } else {
      setSelectedAssetIds(new Set(assets.map(a => a.id)));
    }
  };

  // --- Upload handler ---
  const handleUpload = async (files: File[]) => {
    setUploadingCount(prev => prev + files.length);
    for (const file of files) {
      try {
        const img = await loadImage(file);
        await adminApi.assets.upload(file, {
          width: img.naturalWidth,
          height: img.naturalHeight,
        }, selectedCategoryId ?? undefined);
      } catch (err) {
        console.error('Upload failed:', err);
      } finally {
        setUploadingCount(prev => prev - 1);
      }
    }
    invalidateAll();
  };

  // --- Category tabs ---
  const categoryTabs = [
    { key: 'all' as const, label: 'All' },
    ...categories.map(c => ({ key: c.id, label: c.name })),
    { key: 'uncategorized' as const, label: 'Uncategorized' },
  ];

  const activeTab = selectedCategoryId ?? 'all';

  const handleTabChange = (key: string) => {
    if (key === 'all') setSelectedCategoryId(null);
    else setSelectedCategoryId(key);
    setSelectedAssetIds(new Set());
  };

  // --- Asset items for grid ---
  const assetItems = assets.map(a => ({
    id: a.id,
    name: a.name,
    src: a.url,
    size: a.size,
    width: a.width,
    height: a.height,
    mimeType: a.mimeType,
    source: a.source,
    hasWarning: a.hasTransparency === false,
    warningText: a.hasTransparency === false ? 'No transparent background detected. The image may cover the garment behind it.' : undefined,
  }));

  return (
    <div className="px-8 py-6 overflow-y-auto h-screen">
      <PageHeader
        icon={ImageIcon}
        title="Asset Management"
        description="Manage design categories and Printtat assets"
        actions={
          <label className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors cursor-pointer">
            <Upload size={16} />
            Upload
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) {
                  handleUpload(Array.from(e.target.files));
                  e.target.value = '';
                }
              }}
            />
          </label>
        }
      />

      {/* Upload zone */}
      <div className="mt-6">
        <UploadZone onFiles={handleUpload} multiple />
      </div>

      {/* Uploading indicator */}
      {uploadingCount > 0 && (
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
          <LoadingState variant="inline" />
          Uploading {uploadingCount} {uploadingCount === 1 ? 'file' : 'files'}...
        </div>
      )}

      {/* Selection mode toolbar */}
      {selectionMode && assets.length > 0 && (
        <div className="mt-6 flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 rounded-lg">
          <Button variant="ghost" size="xs" onClick={exitSelectionMode}>
            <X size={12} className="mr-1" />
            Cancel
          </Button>
          <div className="h-3.5 w-px bg-blue-200" />
          <button
            onClick={toggleSelectAll}
            className="text-xs font-medium text-blue-700 hover:text-blue-900 transition-colors"
          >
            {selectedAssetIds.size === assets.length ? 'Deselect all' : 'Select all'}
          </button>
          <span className="text-xs text-blue-600">
            {selectedAssetIds.size} of {assets.length} selected
          </span>
          {selectedAssetIds.size > 0 && (
            <>
              <div className="h-3.5 w-px bg-blue-200" />
              <Button
                variant="solid"
                size="xs"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => setConfirmBulkDelete(true)}
              >
                <Trash2 size={12} className="mr-1" />
                Delete ({selectedAssetIds.size})
              </Button>
            </>
          )}
        </div>
      )}

      {/* Category bar: Select / Tabs / New Category */}
      {!selectionMode && (
        <div className="mt-6 flex items-center justify-between gap-4">
          {/* Left: Select */}
          <div className="shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectionMode(true)}
              disabled={assets.length === 0}
            >
              <CheckSquare size={14} className="mr-1" />
              Select
            </Button>
          </div>

          {/* Center: Category tabs */}
          <div className="flex-1 min-w-0 flex justify-center">
            {categoriesLoading ? (
              <LoadingState variant="inline" />
            ) : editingCategoryId ? (
              <div className="flex items-center gap-1">
                <TextField
                  autoFocus
                  value={editingCategoryName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingCategoryName(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' && editingCategoryName.trim()) {
                      updateCategory.mutate({ id: editingCategoryId, name: editingCategoryName.trim() });
                    }
                    if (e.key === 'Escape') setEditingCategoryId(null);
                  }}
                  className="w-28 text-xs"
                />
                <Button size="xs" onClick={() => {
                  if (editingCategoryName.trim()) updateCategory.mutate({ id: editingCategoryId, name: editingCategoryName.trim() });
                }}>
                  Save
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setEditingCategoryId(null)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Tabs
                tabs={categoryTabs}
                value={activeTab}
                onChange={handleTabChange}
                variant="pill"
                renderTab={(tab, isActive) => {
                  const category = categories.find(c => c.id === tab.key);
                  if (!category) return null;
                  return (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          className={`inline-flex items-center justify-center w-4 h-4 rounded-full transition-colors ${
                            isActive ? 'hover:bg-gray-700' : 'hover:bg-gray-300'
                          }`}
                        >
                          <MoreVertical size={11} />
                        </span>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem onClick={() => {
                          setEditingCategoryId(category.id);
                          setEditingCategoryName(category.name);
                        }}>
                          <Pencil size={14} className="mr-2" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => setDeletingCategory(category)}
                        >
                          <Trash2 size={14} className="mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }}
              />
            )}
          </div>

          {/* Right: New Category */}
          <div className="shrink-0">
            {isCreatingCategory ? (
              <div className="flex items-center gap-1">
                <TextField
                  autoFocus
                  placeholder="Category name"
                  value={newCategoryName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' && newCategoryName.trim()) createCategory.mutate(newCategoryName.trim());
                    if (e.key === 'Escape') { setIsCreatingCategory(false); setNewCategoryName(''); }
                  }}
                  className="w-36"
                />
                <Button
                  size="sm"
                  onClick={() => { if (newCategoryName.trim()) createCategory.mutate(newCategoryName.trim()); }}
                  loading={createCategory.isPending}
                >
                  Create
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setIsCreatingCategory(false); setNewCategoryName(''); }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsCreatingCategory(true)}
              >
                <Plus size={14} className="mr-1" />
                New Category
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Asset grid */}
      <div className="mt-6">
        {assetsLoading ? (
          <LoadingState variant="skeleton" columns={4} />
        ) : assetItems.length === 0 ? (
          <EmptyState
            icon={FolderOpen}
            title={selectedCategoryId === 'uncategorized' ? 'No uncategorized assets' : selectedCategoryId ? 'No assets in this category' : 'No Printtat assets yet'}
            description="Upload images above to add assets to the library"
          />
        ) : (
          <AssetGrid
            assets={assetItems}
            columns={4}
            showControls={!selectionMode}
            selectable={selectionMode}
            selectedIds={selectedAssetIds}
            onSelect={toggleAssetSelection}
            onAssetClick={(item) => setPreviewAssetId(item.id)}
            onDelete={(id) => setDeletingAssetId(id)}
            onRename={(id, newName) => renameAsset.mutate({ id, name: newName })}
            onChangeCategory={(id) => {
              const asset = assets.find(a => a.id === id);
              setChangingCategoryAssetId(id);
              setSelectedNewCategoryId(asset?.categoryId ?? '');
            }}
          />
        )}
      </div>

      {/* Asset preview overlay */}
      {previewAssetId && (() => {
        const asset = assets.find(a => a.id === previewAssetId);
        if (!asset) return null;
        return (
          <AssetPreviewOverlay
            asset={asset}
            onClose={() => setPreviewAssetId(null)}
            onRename={(id, newName) => renameAsset.mutate({ id, name: newName })}
            onDelete={(id) => { setPreviewAssetId(null); setDeletingAssetId(id); }}
          />
        );
      })()}

      {/* Delete category confirmation */}
      <AlertDialog
        open={!!deletingCategory}
        onOpenChange={(open) => { if (!open) setDeletingCategory(null); }}
        title="Delete category"
        description={deletingCategory ? `Delete "${deletingCategory.name}"? Assets in this category will become uncategorized — they won't be deleted.` : ''}
        confirmText="Delete"
        variant="destructive"
        loading={deleteCategory.isPending}
        onConfirm={() => { if (deletingCategory) deleteCategory.mutate(deletingCategory.id); }}
        onCancel={() => setDeletingCategory(null)}
      />

      {/* Delete asset confirmation */}
      <AlertDialog
        open={!!deletingAssetId}
        onOpenChange={(open) => { if (!open) setDeletingAssetId(null); }}
        title="Delete asset"
        description="This will permanently delete this Printtat asset from the library."
        confirmText="Delete"
        variant="destructive"
        loading={deleteAsset.isPending}
        onConfirm={() => { if (deletingAssetId) deleteAsset.mutate(deletingAssetId); }}
        onCancel={() => setDeletingAssetId(null)}
      />

      {/* Bulk delete confirmation */}
      <AlertDialog
        open={confirmBulkDelete}
        onOpenChange={(open) => { if (!open) setConfirmBulkDelete(false); }}
        title="Delete selected assets"
        description={`This will permanently delete ${selectedAssetIds.size} Printtat ${selectedAssetIds.size === 1 ? 'asset' : 'assets'} from the library.`}
        confirmText={`Delete ${selectedAssetIds.size}`}
        variant="destructive"
        loading={bulkDeleteAssets.isPending}
        onConfirm={() => bulkDeleteAssets.mutate(Array.from(selectedAssetIds))}
        onCancel={() => setConfirmBulkDelete(false)}
      />

      {/* Change category dialog */}
      {changingCategoryAssetId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setChangingCategoryAssetId(null); setSelectedNewCategoryId(''); }}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">Change Category</h3>
            <p className="text-sm text-gray-500 mb-4">Select a category for this asset.</p>
            <Select
              size="md"
              value={selectedNewCategoryId}
              onChange={(e) => setSelectedNewCategoryId(e.target.value)}
              options={[
                { value: '', label: 'Uncategorized' },
                ...categories.map(c => ({ value: c.id, label: c.name })),
              ]}
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setChangingCategoryAssetId(null); setSelectedNewCategoryId(''); }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                loading={changeAssetCategory.isPending}
                onClick={() => {
                  changeAssetCategory.mutate({
                    id: changingCategoryAssetId,
                    categoryId: selectedNewCategoryId || null,
                  });
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Helper ---
const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
